import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Bell, Check, CheckCheck, X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const dropdownRef = useRef(null);
  const eventSourceRef = useRef(null);
  const navigate = useNavigate();

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications?limit=20');
      if (response.data) {
        setNotifications(response.data);
        setUnreadCount(response.data.filter(n => !n.is_read && !n.read).length);
      }
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    }
  }, []);

  // Setup SSE connection
  useEffect(() => {
    let reconnectTimeout = null;
    let retryCount = 0;
    const maxRetries = 5;

    const connectSSE = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const eventSource = new EventSource(`${API_URL}/api/notifications/stream`, {
        withCredentials: true
      });

      eventSource.onopen = () => {
        logger.log('SSE connected');
        setConnected(true);
        retryCount = 0;
      };

      eventSource.addEventListener('connected', (event) => {
        logger.log('SSE connection confirmed:', event.data);
        fetchNotifications();
      });

      eventSource.addEventListener('unread_count', (event) => {
        try {
          const data = JSON.parse(event.data);
          setUnreadCount(data.count);
        } catch (e) {
          logger.error('Error parsing unread_count:', e);
        }
      });

      eventSource.addEventListener('notification', (event) => {
        try {
          const notification = JSON.parse(event.data);
          logger.log('New notification received:', notification);
          
          // Add to list
          setNotifications(prev => [notification, ...prev.slice(0, 19)]);
          setUnreadCount(prev => prev + 1);
          
          // Play sound
          playNotificationSound();
          
          // Show browser notification if permitted
          showBrowserNotification(notification);
        } catch (e) {
          logger.error('Error parsing notification:', e);
        }
      });

      eventSource.addEventListener('ping', () => {
        // Keepalive ping, ignore
      });

      eventSource.onerror = (error) => {
        logger.error('SSE error:', error);
        setConnected(false);
        eventSource.close();

        // Reconnect with exponential backoff
        if (retryCount < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
          retryCount++;
          logger.log(`Reconnecting SSE in ${delay}ms (attempt ${retryCount}/${maxRetries})`);
          reconnectTimeout = setTimeout(connectSSE, delay);
        }
      };

      eventSourceRef.current = eventSource;
    };

    // Initial fetch and SSE setup
    fetchNotifications();
    connectSSE();

    // Request browser notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [fetchNotifications]);

  // NOTA: il listener "click fuori → chiudi" è gestito SOLO dentro NotificationDropdown,
  // che conosce sia il Portal sia il bottone campanella. Un listener qui chiuderebbe
  // il dropdown al mousedown prima che il click sulla notifica venga registrato (BUG FIX).

  const playNotificationSound = () => {
    try {
      // Create a simple beep using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (e) {
      // Audio not supported
    }
  };

  const showBrowserNotification = (notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification.id
      });
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      logger.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await api.put('/notifications/read-all');
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read: true })));
      setUnreadCount(0);
    } catch (error) {
      logger.error('Error marking all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Mappa i link delle notifiche alle route corrette del frontend.
   * Gestisce link legacy e route inesistenti.
   */
  const normalizeNotificationLink = (link) => {
    if (!link) return '/';
    
    // Route valide del frontend
    const validRoutes = [
      '/leads',
      '/calendar',
      '/estimates',
      '/projects',
      '/contracts',
      '/whatsapp',
      '/payments',
      '/settings',
      '/users',
      '/analytics',
      '/notifications',
      '/profile',
      '/furniture',
      '/price-list',
      '/commissions',
      '/audit-logs',
      '/data-integrity',
      '/system-health',
      '/stats'
    ];
    
    // Mappatura link legacy → route corrette
    // /appointments/:id → /calendar (gli appuntamenti sono nel calendario)
    if (link.startsWith('/appointments/') || link === '/appointments') {
      return '/calendar';
    }
    
    // Verifica se il link base (senza ID) è valido
    const basePath = '/' + link.split('/').filter(Boolean)[0];
    
    // Se il link base è valido, usa il link originale
    if (validRoutes.includes(basePath)) {
      return link;
    }
    
    // Fallback per link non riconosciuti
    logger.warn(`[NotificationBell] Link non riconosciuto: ${link}, redirect a dashboard`);
    return '/';
  };

  const handleNotificationClick = (notification) => {
    // Marca come letta prima della navigazione
    if (!notification.is_read && !notification.read) {
      markAsRead(notification.id);
    }
    
    // Chiudi il dropdown prima della navigazione
    setIsOpen(false);
    
    // Normalizza il link e naviga
    const targetLink = normalizeNotificationLink(notification.link);
    
    // Naviga usando window.location per garantire la navigazione dal Portal
    window.location.href = targetLink;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diff = now - date;
      
      if (diff < 60000) return 'Ora';
      if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)} ore`;
      return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
    } catch {
      return '';
    }
  };

  const getNotificationIcon = (type) => {
    const colors = {
      'success': 'bg-green-100 text-green-600',
      'error': 'bg-red-100 text-red-600',
      'warning': 'bg-yellow-100 text-yellow-600',
      'info': 'bg-blue-100 text-blue-600'
    };
    return colors[type] || colors.info;
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell-btn"
      >
        <Bell className={`h-5 w-5 ${connected ? 'text-gray-600' : 'text-gray-400'}`} />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown rendered as Portal */}
      {isOpen && ReactDOM.createPortal(
        <NotificationDropdown
          notifications={notifications}
          unreadCount={unreadCount}
          loading={loading}
          onClose={() => setIsOpen(false)}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onNotificationClick={handleNotificationClick}
          formatTime={formatTime}
          getNotificationIcon={getNotificationIcon}
          buttonRef={dropdownRef}
        />,
        document.body
      )}
    </div>
  );
}

// Separate Dropdown Component rendered via Portal
function NotificationDropdown({ 
  notifications, 
  unreadCount, 
  loading, 
  onClose, 
  onMarkAsRead, 
  onMarkAllAsRead, 
  onNotificationClick,
  formatTime,
  getNotificationIcon,
  buttonRef
}) {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  // Calculate position based on button location
  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [buttonRef]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, buttonRef]);

  return (
    <div 
      ref={dropdownRef}
      className="fixed w-80 sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden"
      style={{ 
        top: position.top, 
        right: position.right,
        zIndex: 99999,
        maxHeight: 'calc(100vh - 100px)'
      }}
      data-testid="notification-dropdown"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-orange-500" />
          <span className="font-semibold text-gray-900">Notifiche</span>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="bg-orange-100 text-orange-700">
              {unreadCount} nuove
            </Badge>
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onMarkAllAsRead}
            disabled={loading}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            <CheckCheck className="h-3 w-3 mr-1" />
            Segna tutte lette
          </Button>
        )}
      </div>

      {/* Notification List */}
      <div className="max-h-96 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p>Nessuna notifica</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              data-testid={`notification-item-${notification.id}`}
              className={`px-4 py-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                !notification.is_read && !notification.read ? 'bg-orange-50' : ''
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onNotificationClick(notification);
              }}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-full ${getNotificationIcon(notification.type)}`}>
                  <Bell className="h-3 w-3" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-medium ${
                      !notification.is_read && !notification.read ? 'text-gray-900' : 'text-gray-600'
                    }`}>
                      {notification.title}
                    </p>
                    <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                      {formatTime(notification.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{notification.message}</p>
                  {notification.link && (
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onNotificationClick(notification);
                      }}
                      className="flex items-center mt-1 text-xs text-blue-500 hover:text-blue-700 hover:underline cursor-pointer"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Vai ai dettagli
                    </button>
                  )}
                </div>
                {!notification.is_read && !notification.read && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkAsRead(notification.id);
                    }}
                    className="p-1 hover:bg-gray-200 rounded"
                    title="Segna come letta"
                  >
                    <Check className="h-4 w-4 text-gray-400 hover:text-green-500" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2 border-t bg-gray-50 text-center">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-blue-600 hover:text-blue-800"
            onClick={() => {
              onClose();
              window.location.href = '/audit-logs';
            }}
          >
            Vedi tutte le attività
          </Button>
        </div>
      )}
    </div>
  );
}
